import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronRight, Loader2 } from "lucide-react";
import { menuApi } from "@/lib/menuApi";
import { api } from "@/lib/api";
import { toast } from "sonner";
import BackButton from "@/components/BackButton";

/**
 * Two-step guided create flow:
 *   1. Choose a canvas format (portrait / landscape / digital)
 *   2. Pick an approved background matching that orientation
 * The format step filters templates so retailers don't have to sift through
 * every background. After the user picks a template we create the menu record
 * and route into the workspace.
 */
const FORMATS = [
  { id: "letter-portrait", label: "US Letter — Portrait", w: 850, h: 1100, orientation: "portrait" },
  { id: "letter-landscape", label: "US Letter — Landscape", w: 1100, h: 850, orientation: "landscape" },
  { id: "tabloid-portrait", label: "11 × 17 Portrait", w: 1100, h: 1700, orientation: "portrait" },
  { id: "digital-landscape", label: "Digital Screen — Landscape", w: 1920, h: 1080, orientation: "landscape" },
  { id: "digital-portrait", label: "Digital Screen — Portrait", w: 1080, h: 1920, orientation: "portrait" },
  { id: "social", label: "Social / Digital Menu", w: 1080, h: 1080, orientation: "portrait" },
];

export default function MenuDesignerNew() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [format, setFormat] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [name, setName] = useState("Untitled Menu");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [previewProducts, setPreviewProducts] = useState([]);

  useEffect(() => {
    menuApi
      .listTemplates()
      .then(setTemplates)
      .catch(() => toast.error("Could not load templates"))
      .finally(() => setLoading(false));
    // Pull a handful of real products so the mini previews look like a real
    // filled menu instead of empty slot outlines.
    api.get("/products").then((r) => setPreviewProducts(r.data.slice(0, 8))).catch(() => {});
  }, []);

  const eligible = useMemo(() => {
    if (!format) return templates;
    // Match on orientation. Users can still see any template roughly matching
    // the aspect (we don't filter on exact dimensions because Admin may upload
    // slightly-different canvas sizes for the same orientation).
    return templates.filter((t) => t.orientation === format.orientation && t.active !== false);
  }, [templates, format]);

  async function handleCreate() {
    if (!selectedTemplateId) return;
    setCreating(true);
    try {
      const menu = await menuApi.createMenu({ template_id: selectedTemplateId, name });
      nav(`/menu-designer/edit/${menu.id}`);
    } catch {
      toast.error("Could not create menu");
      setCreating(false);
    }
  }

  return (
    <div className="space-y-10 slide-up" data-testid="menu-designer-new">
      <BackButton />
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Menu Designer</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mt-1">Create a New Menu</h1>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.2em]">
        <Step n={1} label="Format" active={step === 1} done={step > 1} />
        <ChevronRight className="h-3 w-3 text-muted-foreground" />
        <Step n={2} label="Background" active={step === 2} done={step > 2} />
      </div>

      {step === 1 && (
        <section>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FORMATS.map((f) => (
              <button
                key={f.id}
                onClick={() => {
                  setFormat(f);
                  setStep(2);
                }}
                className="text-left border border-border rounded-sm bg-surface hover:bg-surface-elev transition p-6 group"
                data-testid={`format-${f.id}`}
              >
                <div
                  className="rounded-sm border border-border bg-muted mb-4"
                  style={{
                    aspectRatio: `${f.w}/${f.h}`,
                    width: f.orientation === "landscape" ? "70%" : "45%",
                  }}
                />
                <div className="font-display text-lg font-bold">{f.label}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                  {f.w} × {f.h}
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {step === 2 && format && (
        <section className="space-y-6">
          <div className="flex items-center gap-4">
            <button onClick={() => setStep(1)} className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition">
              ← Change format
            </button>
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">·</span>
            <span className="text-xs font-mono uppercase tracking-[0.2em]">{format.label}</span>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading backgrounds…</div>
          ) : eligible.length === 0 ? (
            <div className="border border-dashed border-border rounded-sm p-10 text-center text-sm text-muted-foreground">
              No {format.orientation} backgrounds available yet. Ask an admin to upload one under Menu Designer Management.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {eligible.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setSelectedTemplateId(t.id)}
                  className={`text-left border rounded-sm overflow-hidden bg-surface hover:bg-surface-elev transition group ${selectedTemplateId === t.id ? "ring-2 ring-offset-2 ring-offset-background" : "border-border"}`}
                  style={selectedTemplateId === t.id ? { borderColor: "#FBC90C", boxShadow: "0 0 0 2px #FBC90C33" } : {}}
                  data-testid={`template-${t.id}`}
                >
                  <div className="aspect-[3/4] relative overflow-hidden" style={{ background: t.background_color || "#0A0A0A" }}>
                    <LayoutPreview template={t} products={previewProducts} />
                    {selectedTemplateId === t.id && (
                      <div className="absolute top-2 right-2 rounded-full p-1" style={{ background: "#FBC90C", color: "#000" }}>
                        <Check className="h-3.5 w-3.5" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="text-sm font-semibold truncate">{t.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                      {(t.layout?.slots || []).length} slots · {t.canvas_width}×{t.canvas_height}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Name + create */}
          <div className="border border-border rounded-sm bg-surface p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <label className="flex-1 flex items-center gap-3">
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Menu Name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 bg-transparent border border-border rounded-sm px-3 py-2 text-sm"
                data-testid="menu-name-input"
              />
            </label>
            <button
              disabled={!selectedTemplateId || creating}
              onClick={handleCreate}
              className="bg-foreground text-background py-2.5 px-6 uppercase text-xs font-semibold tracking-widest disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="create-menu-btn"
            >
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Create Menu
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function Step({ n, label, active, done }) {
  return (
    <div className={`inline-flex items-center gap-2 ${active ? "" : "text-muted-foreground"}`}>
      <span
        className={`w-6 h-6 rounded-full flex items-center justify-center border ${active || done ? "border-foreground" : "border-border"}`}
        style={done ? { background: "#FBC90C", color: "#000", borderColor: "#FBC90C" } : {}}
      >
        {done ? <Check className="h-3 w-3" /> : n}
      </span>
      {label}
    </div>
  );
}

// Miniature preview of a template's structured layout — populated with real
// product previews so users see roughly what the finished menu looks like
// before they click through. Uses percentage coordinates so it scales into
// any card size cleanly.
function LayoutPreview({ template, products = [] }) {
  const layout = template.layout || {};
  const accent = template.accent_color || "#FBC90C";
  const scaleX = (v) => `${(v / template.canvas_width) * 100}%`;
  const scaleY = (v) => `${(v / template.canvas_height) * 100}%`;
  const demoPrices = ["40", "35", "60", "25", "45", "50", "30", "55"];
  const slots = layout.slots || [];
  const hasCustomBg = Boolean(template.background_url);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const bgSrc = hasCustomBg && template.background_url.startsWith("/")
    ? `${backendUrl}${template.background_url}`
    : template.background_url;
  return (
    <div className="absolute inset-0" style={{ background: template.background_color || "#0A0A0A" }}>
      {hasCustomBg && (
        <img src={bgSrc} alt="" className="absolute inset-0 w-full h-full object-cover" />
      )}
      {/* Header with APOLLO wordmark — skipped for custom backgrounds because
          those already carry Apollo artwork baked into the image. */}
      {!hasCustomBg && layout.header && (
        <div
          className="absolute flex flex-col items-center justify-center"
          style={{
            left: scaleX(layout.header.x),
            top: scaleY(layout.header.y),
            width: scaleX(layout.header.w),
            height: scaleY(layout.header.h),
          }}
        >
          <div
            className="font-mono font-black uppercase"
            style={{ color: "#FFFFFF", fontSize: 13, letterSpacing: "0.32em", lineHeight: 1 }}
          >
            APOLLO
          </div>
          <div
            className="mt-0.5 font-mono uppercase"
            style={{ color: accent, fontSize: 6, letterSpacing: "0.4em", opacity: 0.9 }}
          >
            MENU
          </div>
          <div className="mt-0.5" style={{ width: "40%", height: 1, background: accent }} />
        </div>
      )}

      {/* Filled slots */}
      {slots.map((s, i) => {
        const product = products[i % Math.max(products.length, 1)];
        return (
          <div
            key={s.id}
            className="absolute rounded-sm overflow-hidden"
            style={{
              left: scaleX(s.x),
              top: scaleY(s.y),
              width: scaleX(s.w),
              height: scaleY(s.h),
              background: "rgba(255,255,255,0.04)",
              border: `1px solid ${accent}33`,
            }}
          >
            <SlotPreview product={product} kind={s.kind} accent={accent} price={demoPrices[i % demoPrices.length]} />
          </div>
        );
      })}

      {/* Footer */}
      {!hasCustomBg && layout.footer && (
        <div
          className="absolute flex items-center justify-center"
          style={{
            left: scaleX(layout.footer.x),
            top: scaleY(layout.footer.y),
            width: scaleX(layout.footer.w),
            height: scaleY(layout.footer.h),
            borderTop: `1px solid ${accent}55`,
          }}
        >
          <div
            className="font-mono uppercase"
            style={{ color: "#ffffff88", fontSize: 5, letterSpacing: "0.4em" }}
          >
            ELEVATE BEYOND THE ORDINARY
          </div>
        </div>
      )}
    </div>
  );
}

// One populated slot in the mini preview — shows a product silhouette, name,
// and a demo price so the card feels alive without being misleading.
function SlotPreview({ product, kind, accent, price }) {
  if (!product) return null;
  const isRow = kind === "product_row";
  const isHero = kind === "product_hero";
  if (isRow) {
    return (
      <div className="w-full h-full flex items-center gap-1 px-1">
        {product.hero_image && (
          <div className="h-full aspect-square flex items-center justify-center flex-shrink-0">
            <img src={product.hero_image} alt="" className="max-h-full max-w-full object-contain" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-bold uppercase truncate text-white" style={{ fontSize: 7, letterSpacing: 0.5 }}>{product.name}</div>
          <div className="text-white/60 truncate uppercase" style={{ fontSize: 5, letterSpacing: 0.5 }}>{product.strain}</div>
        </div>
        <div className="font-bold flex-shrink-0" style={{ color: accent, fontSize: 9 }}>${price}</div>
      </div>
    );
  }
  if (isHero) {
    return (
      <div className="w-full h-full flex items-center gap-1.5 px-1.5">
        {product.hero_image && (
          <div className="h-full flex-shrink-0 flex items-center justify-center" style={{ width: "45%" }}>
            <img src={product.hero_image} alt="" className="max-h-full max-w-full object-contain" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-bold uppercase text-white leading-tight" style={{ fontSize: 11, letterSpacing: 0.5 }}>{product.name}</div>
          <div className="text-white/70" style={{ fontSize: 7 }}>{product.strain}</div>
          <div className="font-bold mt-0.5" style={{ color: accent, fontSize: 14 }}>${price}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="w-full h-full flex flex-col items-center justify-between px-1 py-1 text-center">
      {product.hero_image && (
        <div className="flex-1 min-h-0 w-full flex items-center justify-center py-0.5">
          <img src={product.hero_image} alt="" className="max-h-full max-w-full object-contain" />
        </div>
      )}
      <div className="w-full flex flex-col items-center leading-tight">
        <div className="font-bold uppercase text-white truncate w-full" style={{ fontSize: 6.5, letterSpacing: 0.4 }}>{product.name}</div>
        <div className="text-white/60 truncate w-full uppercase" style={{ fontSize: 5, letterSpacing: 0.4 }}>{product.strain}</div>
        <div className="font-bold" style={{ color: accent, fontSize: 9 }}>${price}</div>
      </div>
    </div>
  );
}
